import { CSSInterpolation } from '@emotion/css/create-instance'
import React, { type ChangeEvent, type Ref, useState } from 'react'

import type { ReactInputProps } from '../../types'
import { INPUT_MIN_USABLE_WIDTH } from '../../util/commonStyles'
import { getIconButtonSize, IconButton } from './IconButton'
import {
  DEFAULT_SIZES,
  type InputState,
  InternalInput,
} from './internal/InternalInput'
import { InternalInputWrapper } from './internal/InternalInputWrapper'

type Props = {
  /**
   * The value to show in the input. If empty, any provided `placeholder` is shown.
   */
  value: string
  /**
   * Callback called when the value in the input changes. As a convenience, the value is provided
   * separately from the change event for use cases that just need the new value.
   */
  onChange: ({
    event,
    value,
  }: {
    event: ChangeEvent<HTMLInputElement>
    value: string
  }) => void
  /**
   * Whether to disable the input or mark it as having an error.
   */
  state?: InputState
  /**
   * Provide `true` to visually hide the input label, while still leaving it present in the DOM
   * for screen readers. Ignored if no `label` is provided.
   */
  hideLabel?: boolean
  /**
   * A label to show above the input. If you would like to visually hide it please use
   * `hideLabel` prop to do so. If you do not provide a value here, you must provide your own
   * `<label>` element for the input for accessibility reasons.
   */
  label?: string
  /**
   * An error message to show below the input. Will only be shown if `state` is `"error"` and
   * this component also has a `label` specified. If another component is rendering the `label`
   * for this input, that component should be rendering the error message as well.
   */
  errorMessage?: string
  /**
   * A React ref to attach to the actual `input` element, e.g. for triggering focus or blur on
   * the input.
   */
  innerRef?: Ref<HTMLInputElement>
  /**
   * Pass a CSS object if you need to override styles on the root element. Please do this sparingly.
   */
  overrideRootStyles?: CSSInterpolation
  /**
   * This is the accessible name for the icon button that reveals the password in plain text to the user.
   */
  showPasswordLabel?: string
}

type DefaultInputProps = Omit<
  ReactInputProps,
  keyof Props | 'disabled' | 'onChange' | 'css' | 'className' | 'style' | 'type'
>

/**
 * An input element for collecting passwords. By default the input value is hidden, but the
 * user can toggle this setting.
 *
 * Like all inputs, this should be used inside a `<form>` element. You also may want to use
 * the `getInputState` helper.
 */
export function PasswordInput({
  errorMessage,
  hideLabel = false,
  innerRef,
  label = '',
  onChange,
  overrideRootStyles,
  state = 'default',
  value,
  ...inputProps
}: Props & DefaultInputProps) {
  const [showText, setShowText] = useState(false)
  const iconToShow = showText ? 'EyeClosed16' : 'Eye16'

  const sizes = {
    ...DEFAULT_SIZES,
    // Provide some space between the input and the IconButton.
    inputMarginRight: 8,
    // The right padding is smaller because the IconButton has some padding of its own.
    paddingRight: 8,
  }

  return (
    <InternalInputWrapper
      errorMessage={errorMessage}
      hideLabel={hideLabel}
      label={label}
      minWidth={
        INPUT_MIN_USABLE_WIDTH +
        2 * sizes.border +
        sizes.paddingLeft +
        sizes.paddingRight +
        sizes.inputMarginRight +
        getIconButtonSize(iconToShow)
      }
      overrideRootStyles={overrideRootStyles}
      state={state}
    >
      {errorMessageId => (
        <InternalInput
          aria-describedby={errorMessageId}
          endControl={
            <IconButton
              aria-label="show password in plain text"
              aria-pressed={showText}
              icon={iconToShow}
              onClick={event => {
                event.preventDefault()
                setShowText(shown => !shown)
              }}
              overrideRootStyles={{
                // IconButtons have a white background by default when disabled, but we want it to
                // match the input instead.
                backgroundColor:
                  state === 'disabled' ? 'transparent' : undefined,
                // Ensure that the IconButton doesn't grow or shrink.
                flex: '0 0 auto',
              }}
              state={state === 'disabled' ? 'disabled' : false}
              type="button"
            />
          }
          innerRef={innerRef}
          onChange={onChange}
          overrideInputStyles={{
            '&::-ms-reveal': {
              // Hide native password reveal button in Edge
              // https://docs.microsoft.com/en-us/microsoft-edge/web-platform/password-reveal
              display: 'none',
            },
          }}
          sizes={sizes}
          state={state}
          type={showText ? 'text' : 'password'}
          value={value}
          {...inputProps}
        />
      )}
    </InternalInputWrapper>
  )
}
